import React from "react";
import { Download, Maximize2, X } from "lucide-react";
import { useUpdateEnrollmentStatusMutation } from "../../services/enrollment/enrollmentApi";

const FieldRow = ({ label, value }) => (
  <div>
    <p className="mb-1 text-sm font-medium text-[#374151]">{label}</p>
    <div className="rounded-lg bg-[#D1D5DB] px-4 py-3 text-sm text-[#111827]">
      {value}
    </div>
  </div>
);

const PaymentDetailsModal = ({ isOpen, onClose, purchase }) => {
  const [updateEnrollmentStatus, { isLoading }] =
    useUpdateEnrollmentStatusMutation();
  const isAlreadyApproved =
    String(purchase?.status || "").toLowerCase() === "approved";

  if (!isOpen || !purchase) {
    return null;
  }

  const handleDownloadScreenshot = async () => {
    const screenshotUrl = purchase.paymentScreenshot;
    if (!screenshotUrl) {
      return;
    }

    try {
      const response = await fetch(screenshotUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch screenshot");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const urlPath = screenshotUrl.split("?")[0];
      const extensionMatch = urlPath.match(/\.([a-zA-Z0-9]+)$/);
      const extension = extensionMatch?.[1] ? `.${extensionMatch[1]}` : ".png";

      link.href = blobUrl;
      link.download = `payment-${purchase.orderId || purchase.id}${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download payment screenshot:", error);
      window.open(screenshotUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleExpandScreenshot = () => {
    const screenshotUrl = purchase.paymentScreenshot;
    if (!screenshotUrl) {
      return;
    }

    window.open(screenshotUrl, "_blank", "noopener,noreferrer");
  };

  const handleApprove = async () => {
    try {
      await updateEnrollmentStatus({ id: purchase.id, status: "approved" }).unwrap();
      onClose();
    } catch (error) {
      console.error("Failed to approve enrollment:", error);
    }
  };
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-[#6B6363]/80 px-4 py-6">
      <div className="flex max-h-[92vh] w-full max-w-[620px] flex-col overflow-hidden rounded-[28px] bg-white shadow-[0_20px_60px_rgba(0,0,0,0.22)]">
        <div className="flex items-center justify-between border-b border-[#D1D5DB] px-7 py-5">
          <h3 className="text-xl font-semibold text-[#111827]">View Detail</h3>
          <button
            type="button"
            aria-label="Close payment details"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[#111827] transition hover:bg-[#F3F4F6]"
          >
            <X size={26} strokeWidth={1.8} />
          </button>
        </div>

        <div className="overflow-y-auto px-7 py-6">
          <section>
            <h4 className="text-lg font-semibold text-[#111827]">Payment Screenshot</h4>
            <div className="mt-3 rounded-[18px] border border-dashed border-[#C9C9C9] p-5">
              {purchase.paymentScreenshot ? (
                <div className="relative">
                  <img
                    src={purchase.paymentScreenshot}
                    alt={purchase.id}
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute bottom-3 right-3 flex items-center gap-2">
                    <button
                      type="button"
                      aria-label="Download payment screenshot"
                      onClick={handleDownloadScreenshot}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-[#111827] shadow-md transition hover:bg-white"
                    >
                      <Download size={18} />
                    </button>
                    <button
                      type="button"
                      aria-label="View full size payment screenshot"
                      onClick={handleExpandScreenshot}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-[#111827] shadow-md transition hover:bg-white"
                    >
                      <Maximize2 size={18} />
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          <section className="mt-5 rounded-[10px] border border-[#E5E7EB] p-5">
            <h4 className="text-lg font-semibold text-[#111827]">Student Details</h4>
            <div className="mt-5 space-y-4">
              <FieldRow label="Full Name" value={purchase.studentName} />
              <FieldRow label="Email Address" value={purchase.studentEmail} />
              <FieldRow label="Phone Number" value={purchase.phoneNumber} />
            </div>
          </section>

          <section className="mt-5 rounded-[10px] border border-[#E5E7EB] p-5">
            <h4 className="text-lg font-semibold text-[#111827]">Course Detail</h4>
            <div className="mt-5 space-y-4">
              <FieldRow label="Course Name" value={purchase.course} />
              <FieldRow label="Course Type" value={purchase.type} />
              <FieldRow label="Phone Number" value="+92 300 1234567" />
            </div>
          </section>

          <div className="mt-10 flex items-center justify-end gap-4 pb-2">
            <button
              type="button"
              onClick={onClose}
              className="min-w-[128px] rounded border border-[#111827] bg-white px-5 py-2.5 text-base font-semibold text-[#111827]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApprove}
              disabled={isLoading || isAlreadyApproved}
              className="min-w-[128px] rounded border border-[#923D8C] bg-[#923D8C] px-5 py-2.5 text-base font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isLoading
                ? "Approving..."
                : isAlreadyApproved
                  ? "Approved"
                  : "Approve"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailsModal;
